import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import { Box, Button } from '@mui/material';
import { useGetAllCustomer } from '../../Query/CustomerQuery';
import { Autocomplete, TextField } from '@mui/material';
import { COLORS } from '../../../theme/themeConstants';
import { createShortTextValidationSchema } from '../../HelperFunctions/validationUtils';
import { useSendNewsletter, useGetNewsletterSubscribers } from '../../Query/NewsletterQuery';
import { SendNewsletterRequest } from '../../../client-sdk';


const validationSchema = Yup.object().shape({
    emailTo: Yup.string().required('Please select an option'),
    subject: createShortTextValidationSchema('Subject', 3),
    message: Yup.string()
        .required('Message is required')
        .test('comprehensive-validation', 'Invalid message format', function(value: any) {
            if (!value) return true; // Let required handle empty values
            
            // Strip HTML tags from rich text content for validation checks
            const textContent = value.replace(/<[^>]*>/g, '');
            
            // Check if empty after stripping HTML
            if (!textContent || textContent.trim().length === 0) {
                return (this as any).createError({ message: 'Message cannot be empty' });
            }
            
            // Check minimum length
            if (textContent.trim().length < 10) {
                return (this as any).createError({ message: 'Message must be at least 10 characters long' });
            }
            
            // Check maximum length
            if (textContent.length > 5000) {
                return (this as any).createError({ message: 'Message cannot exceed 5000 characters' });
            }
            
            return true;
        }),
});


const AddNewsLetter = ({ onCancel }: { onCancel?: () => void }) => {
    // No edit mode for now, so no atom/state for editing
    const { data: customerData} = useGetAllCustomer({ page: 1, limit: 100 ,sortBy:"lastAdded",
    sortOrder: "desc"});
    const { data: subscriberData } = useGetNewsletterSubscribers({ page: 1, limit: 100, sortBy: 'email', sortOrder: 'asc' });
    const { mutate: sendNewsletter, isPending } = useSendNewsletter();
    
    console.log("customerData",customerData)
    console.log("subscriberData",subscriberData)

    const customerOptions = customerData?.data?.map((item:any) => ({
        value: item.userId,
        label: item.name,
    }));

    const subscriberOptions = subscriberData?.subscribers
        ?.filter((item: any) => item.isActive === true)
        ?.map((item: any) => {
            // Extract name from email (part before @)
            const name = item.email.split('@')[0];
            return {
                value: item.id,
                label: name,
            };
        }) || [];

    console.log("customerOptions",customerOptions)
    console.log("subscriberOptions",subscriberOptions)

    return (
        <>
            {AllForm({
                initialValues: {
                    emailTo: 'all',
                    subject: '',
                    message: '',
                    subscriber: '',
                },
                validationSchema,
                validateOnChange: true,
                enableReinitialize: true,
                onSubmit: async (values: any) => {
                    console.log('Newsletter values:', values);
                    
                    try {
                        let newsletterRequest: SendNewsletterRequest;
                        
                        switch (values.emailTo) {
                            case 'All User':
                                newsletterRequest = {
                                    emailTo: 'allUsers',
                                    subject: values.subject,
                                    message: values.message,
                                };
                                break;
                            case 'All Subscriber':
                                newsletterRequest = {
                                    emailTo: 'allSubscribers',
                                    subject: values.subject,
                                    message: values.message,
                                };
                                break;
                            case 'Particular User':
                                newsletterRequest = {
                                    emailTo: 'particularUsers',
                                    customerIds: [values.customer],
                                    subject: values.subject,
                                    message: values.message,
                                };
                                break;
                            case 'Particular Subscriber':
                                newsletterRequest = {
                                    emailTo: 'particularSubscriber',
                                    subscriberIds: [values.subscriber],
                                    subject: values.subject,
                                    message: values.message,
                                };
                                break;
                            default:
                                throw new Error('Invalid email target selected');
                        }
                        
                        sendNewsletter(newsletterRequest, {
                            onSuccess: () => {
                                onCancel?.();
                            }
                        });
                    } catch (error) {
                        console.error('Error sending newsletter:', error);
                    }
                },
                fields: [
                    {
                        name: 'emailTo',
                        label: 'E-Mail To:',
                        type: 'radio',
                        radioOptions: ['All User', 'All Subscriber', 'Particular User', 'Particular Subscriber'],
                        isRequired: true,
                    },
                    {
                        name: 'customer',
                        label: 'Customer:',
                        type: 'custom',
                        dependsOn: { field: 'emailTo', value: 'Particular User' },
                        isRequired: true,
                        component: ({ field, form }: any) => {
                            const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
                            return (
                                <Box sx={{ width: 400 }}>
                                    <Autocomplete
                                        options={(customerOptions ?? []) as { value: string; label: string }[]}
                                        getOptionLabel={(option) => option.label}
                                        value={(customerOptions ?? []).find((opt: { value: string; label: string }) => opt.value === field.value) || null}
                                        onChange={(_, newValue) => {
                                            form.setFieldValue(field.name, newValue ? newValue.value : '');
                                            form.setFieldTouched(field.name, true, false);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={hasError}
                                                placeholder="Select Customer"
                                                sx={{
                                                    mb: 0,
                                                    '& .MuiInputBase-root': {
                                                        fontSize: 14,
                                                        color: 'text.primary',
                                                        width: '400px',
                                                        height: '40px',
                                                    },
                                                    '& .MuiFormLabel-root': {
                                                        fontSize: 14,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            );
                        },
                    },
                    {
                        name: 'subscriber',
                        label: 'Subscriber :',
                        type: 'custom',
                        dependsOn: { field: 'emailTo', value: 'Particular Subscriber' },
                        isRequired: true,
                        component: ({ field, form }: any) => {
                            const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
                            return (
                                <Box sx={{ width: 400 }}>
                                    <Autocomplete
                                        options={subscriberOptions}
                                        getOptionLabel={(option: { value: string; label: string }) => option.label}
                                        value={subscriberOptions.find((opt: { value: string; label: string }) => opt.value === field.value) || null}
                                        onChange={(_, newValue) => {
                                            form.setFieldValue(field.name, newValue ? newValue.value : '');
                                            form.setFieldTouched(field.name, true, false);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={hasError}
                                                placeholder="Select Subscriber"
                                                sx={{
                                                    mb: 0,
                                                    '& .MuiInputBase-root': {
                                                        fontSize: 14,
                                                        color: 'text.primary',
                                                        width: '400px',
                                                        height: '40px',
                                                    },
                                                    '& .MuiFormLabel-root': {
                                                        fontSize: 14,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            );
                        },
                    },
                    {
                        name: 'subject',
                        label: 'Subject:',
                        type: 'text',
                        isRequired: true,
                    },
                    {
                        name: 'message',
                        label: 'Message:',
                        type: 'markdown',
                        isRequired: true,
                    }
                ],
                customButton: (formikProps: any) => (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={formikProps.handleSubmit}
                            disabled={isPending}
                            sx={{
                                backgroundColor: COLORS.primary.main,
                                textTransform: 'none',
                                '&:hover': { backgroundColor: COLORS.primary.dark },
                            }}
                        >
                            {isPending ? 'Sending...' : 'Submit'}
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={formikProps.resetForm}
                            sx={{
                                textTransform: 'none',
                                color: COLORS.secondary.main,
                                borderColor: COLORS.secondary.main,
                            }}
                        >
                            Reset
                        </Button>
                        {onCancel && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={onCancel}
                                sx={{
                                    bgcolor: '#f44336',
                                    color: '#fff',
                                    '&:hover': {
                                        bgcolor: '#d32f2f',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                ),
            })()}
        </>
    );
};

export default AddNewsLetter;

